import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatCurrency, formatDate, formatDateTime, APPOINTMENT_STATUS } from "@/lib/constants";
import { Suggest, ProcedureSearch, amt, round2, lineMoney } from "@/components/crm/pickers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, FileText, Stethoscope, Receipt, IndianRupee, Trash2, ClipboardList,
  Activity, Pill, Paperclip, ChevronDown, AlertTriangle, FlaskConical,
  FileSignature, Printer, Wallet, X, ZoomIn, ZoomOut, RotateCcw, RotateCw, Maximize,
} from "lucide-react";

/* The patient record, organised by VISIT rather than by package.
 *
 * Our patient page grew around the package: treatment cards with sessions
 * nested inside. That works for a 6-session laser course and works for nothing
 * else — a one-off consultation, a walk-in procedure or a review has no package
 * to hang off, so it simply never appeared. Practo Ray (which this clinic runs
 * today, and whose data is being imported) keys everything on the appointment,
 * and so does the way the staff talk about their day.
 *
 * Packages have not gone anywhere; they moved to their own tab, because
 * tracking a multi-session course is still the thing we do that Ray cannot. */

function statusLabel(status) {
  return (APPOINTMENT_STATUS.find((s) => s.value === status) || {}).label || status;
}

function statusTone(status) {
  return (APPOINTMENT_STATUS.find((s) => s.value === status) || {}).color
    || "bg-gray-100 text-gray-600";
}

// The prescription PDF has always existed; nothing on the chart pointed at it.
// Generate then open, because the file is rendered on demand rather than kept.
async function printPrescription(consultationId) {
  try {
    await api.post(`/consultations/${consultationId}/generate-pdf`);
    const res = await api.get(`/consultations/${consultationId}/prescription-pdf`,
                              { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank");
    // Revoke late: revoking immediately races the new tab's own load.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail) || "Could not build the prescription");
  }
}

// The seven record types a visit can carry, in Ray's order. `has` decides
// whether the option is already satisfied for a given visit. `multi` marks the
// ones whose editor holds the WHOLE list rather than a single record — those
// must stay clickable, because opening them again is how you add a second
// procedure or correct the first.
const RECORD_TYPES = [
  { kind: "vitals", label: "Vital Signs", icon: Activity,
    has: (v) => !!v.consultation?.vitals },
  { kind: "notes", label: "Clinical Notes", icon: Stethoscope,
    has: (v) => !!(v.consultation?.chief_complaint || v.consultation?.clinical_notes
                   || v.consultation?.diagnoses?.length) },
  { kind: "prescription", label: "Prescriptions", icon: Pill,
    has: (v) => !!v.consultation?.prescription_items?.length },
  { kind: "files", label: "Files", icon: Paperclip,
    has: (v) => !!v.consultation?.documents?.length },
  { kind: "lab", label: "Lab Orders", icon: FlaskConical,
    has: (v) => !!v.lab_orders?.length },
  { kind: "planned", label: "Treatment Plan", icon: FileText, multi: true,
    has: (v) => !!v.treatment_plan?.length },
  { kind: "completed", label: "Completed Procedures", icon: ClipboardList, multi: true,
    has: (v) => !!v.procedures?.length },
  { kind: "consent", label: "Consent Form", icon: FileSignature,
    has: (v) => !!v.consents?.length },
];

// Vitals, notes, prescriptions and files all live on the CONSULTATION, which
// may not exist yet for a visit nobody opened. Creating one is idempotent —
// the backend returns the existing record when there is one.
async function ensureConsultation(visit) {
  const { data } = await api.post("/consultations", {
    appointment_id: visit.id, patient_id: visit.patient_id,
  });
  return data;
}

/* Records are edited INSIDE the visit they belong to, not in a modal over it.
 * A modal hides the very thing you are writing about — the doctor loses sight
 * of the note they are extending and the procedures already on the visit. Ray
 * expands the form in place, and the gold rule is what marks it as live. */
function EditorPanel({ title, onCancel, onSave, saving, children, disabled }) {
  return (
    <div className="rounded-lg border-l-4 border-gold bg-cream/30 p-4" data-testid="inline-editor">
      <p className="mb-3 font-display text-sm font-semibold text-navy">{title}</p>
      {children}
      <div className="mt-3 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={onSave} disabled={saving || disabled}
                className="bg-navy text-white hover:bg-navy-hover" data-testid="save-record-btn">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function Money({ value, className = "" }) {
  return <span className={className}>{formatCurrency(value || 0)}</span>;
}


/* ─────────────────────────── Charting ─────────────────────────── */

export function ChartingTab({ patientId, patient, onChanged }) {
  const [visits, setVisits] = useState(null);
  const [adding, setAdding] = useState(null);   // {visit, kind}
  const [newChart, setNewChart] = useState(false);

  const load = useCallback(() => {
    api.get(`/patients/${patientId}/timeline`)
      .then((r) => setVisits(r.data.visits || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  if (!visits) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  const newChartButton = (
    <button onClick={() => setNewChart(true)} data-testid="create-chart-btn"
      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-navy/20 bg-white py-4 text-sm font-medium text-navy transition-colors hover:border-navy/40 hover:bg-nude/30">
      <Plus className="h-4 w-4" /> Create a new chart
    </button>
  );

  const dialog = (
    <NewChartDialog
      open={newChart} patientId={patientId} patient={patient}
      onClose={() => setNewChart(false)}
      onCreated={() => { setNewChart(false); load(); onChanged?.(); }}
    />
  );

  if (!visits.length) {
    return (
      <div className="space-y-4">
        {newChartButton}
        <p className="py-6 text-center text-sm text-muted-foreground">
          No visits yet. Records attach to a visit — create a chart for one that already
          happened, or book an appointment.
        </p>
        {dialog}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {newChartButton}
      {dialog}
      {visits.map((v) => (
        <VisitCard key={v.id} visit={v} onAdd={(kind) => setAdding({ visit: v, kind })}
                   editing={adding?.visit?.id === v.id ? adding.kind : null}
                   onCancelEdit={() => setAdding(null)}
                   onSavedEdit={() => { setAdding(null); load(); onChanged?.(); }}
                   onChanged={() => { load(); onChanged?.(); }} />
      ))}
    </div>
  );
}

function VisitCard({ visit: v, onAdd, onChanged, editing, onCancelEdit, onSavedEdit }) {
  const navigate = useNavigate();
  const c = v.consultation;
  const hasAnything = c || v.sessions?.length || v.procedures?.length
    || v.treatment_plan?.length || v.invoices?.length;

  const removeItem = async (id) => {
    try {
      await api.delete(`/visit-items/${id}`);
      toast.success("Removed");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // A quote becomes a bill only when the work is actually done. Without this the
  // plan was a dead end: you could estimate, and the invoice route would then
  // (correctly) refuse to bill an estimate, with no way across.
  const completeItem = async (id) => {
    try {
      await api.put(`/visit-items/${id}`, { kind: "completed" });
      toast.success("Marked as done — it can be billed now");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const invoiceItems = async () => {
    const ids = (v.procedures || []).filter((p) => !p.invoice_id).map((p) => p.id);
    if (!ids.length) { toast.error("Nothing left to bill on this visit"); return; }
    try {
      const r = await api.post(`/appointments/${v.id}/items/invoice`, { item_ids: ids });
      toast.success(`${r.data.invoice_number} raised`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-navy/10 bg-white shadow-sm" data-testid="visit-card">
      {/* Visit header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-navy/10 px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg bg-navy/5 leading-none">
            <span className="text-sm font-bold text-navy">{formatDate(v.scheduled_at)?.slice(0, 2)}</span>
            <span className="text-[9px] uppercase text-muted-foreground">
              {new Date(v.scheduled_at).toLocaleDateString(undefined, { month: "short" })}
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-ink">
              {v.service || "Appointment"}{v.doctor_name ? ` with ${v.doctor_name}` : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDateTime(v.scheduled_at)} · {v.duration_minutes || 60} mins
              {v.category ? ` · ${v.category}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(v.status)}`}>
            {statusLabel(v.status)}
          </span>
          {c && (
            <Button size="sm" variant="outline" className="h-8" title="Prescription PDF"
                    onClick={() => printPrescription(c.id)} data-testid="print-visit-btn">
              <Printer className="h-3.5 w-3.5" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8" data-testid="add-records-btn">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add Records <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            {/* One entry per record type, the way Ray lists them — not a single
                "clinical notes & prescription" link out to another page. A type
                already recorded on this visit is greyed rather than hidden, so
                the menu is the same shape every time and its absence is
                information — except for the list editors, which reopen onto
                what is already there. */}
            <DropdownMenuContent align="end">
              {RECORD_TYPES.map(({ kind, label, icon: Icon, has, multi }) => {
                const already = has(v) && !multi;
                return (
                  <DropdownMenuItem key={kind} disabled={already}
                    onClick={() => !already && onAdd(kind)}
                    className={already ? "opacity-40" : ""}
                    data-testid={`add-record-${kind}`}>
                    <Icon className="mr-2 h-4 w-4" /> {label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="space-y-4 px-5 py-4">
        {editing && (
          // Keyed on the record type so switching type REMOUNTS the editor.
          // Without it the new form renders once against the old form's state —
          // choosing Lab Orders straight after Prescriptions drew a row of
          // prescription fields as lab fields until the effect caught up.
          <VisitRecordEditor key={editing} visit={v} kind={editing}
                             onCancel={onCancelEdit} onSaved={onSavedEdit} />
        )}

        {!hasAnything && !editing && (
          <p className="py-3 text-center text-sm text-muted-foreground">No records added yet</p>
        )}

        {c && <ConsultationBlock consultation={c} />}

        {v.sessions?.length > 0 && (
          <Block icon={Activity} title="Package sessions">
            {v.sessions.map((s) => (
              <div key={s.id} className="rounded-lg bg-cream/50 px-3 py-2 text-sm">
                <p className="text-ink">{s.notes || "Session logged"}</p>
                {s.observations && <p className="text-xs text-muted-foreground">{s.observations}</p>}
              </div>
            ))}
          </Block>
        )}

        {v.procedures?.length > 0 && (
          <ItemTable title="Completed procedures" items={v.procedures} onRemove={removeItem}
                     onEdit={() => onAdd("completed")}
                     action={v.procedures.some((p) => !p.invoice_id) && (
                       <Button size="sm" variant="outline" className="h-7 text-xs" onClick={invoiceItems}>
                         <Receipt className="mr-1 h-3 w-3" /> Raise invoice
                       </Button>
                     )} />
        )}

        {v.treatment_plan?.length > 0 && (
          <ItemTable title="Treatment plan (estimate)" items={v.treatment_plan} onRemove={removeItem}
                     onEdit={() => onAdd("planned")} onComplete={completeItem} estimate />
        )}

        {v.lab_orders?.length > 0 && (
          <Block icon={FlaskConical} title="Lab orders">
            {v.lab_orders.map((lo) => (
              <div key={lo.id} className="rounded-lg bg-cream/50 px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-ink">{lo.tests.map((t) => t.name).join(", ")}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${
                    lo.status === "reported" ? "bg-green-100 text-green-800"
                      : lo.status === "cancelled" ? "bg-gray-200 text-gray-600"
                      : "bg-amber-100 text-amber-800"}`}>{lo.status}</span>
                </div>
                {lo.lab_name && <p className="text-xs text-muted-foreground">{lo.lab_name}</p>}
                {lo.result_summary && (
                  <p className="mt-1 text-xs text-ink"><b>Result:</b> {lo.result_summary}</p>
                )}
              </div>
            ))}
          </Block>
        )}

        {v.consents?.length > 0 && (
          <Block icon={FileSignature} title="Consent">
            {v.consents.map((cs) => (
              <div key={cs.id} className="flex items-center justify-between gap-3 rounded-lg bg-cream/50 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate text-ink">{cs.template_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Signed by {cs.signed_by} · witnessed by {cs.witnessed_by_name} · {formatDate(cs.signed_at)}
                  </p>
                </div>
                {cs.signature && (
                  <img src={cs.signature} alt="Signature"
                       className="h-10 w-24 shrink-0 rounded border border-navy/10 bg-white object-contain" />
                )}
              </div>
            ))}
          </Block>
        )}

        {v.invoices?.length > 0 && (
          <Block icon={Receipt} title="Billing">
            {v.invoices.map((inv) => (
              <InvoiceRow key={inv.id} invoice={inv} onPaid={onChanged} />
            ))}
          </Block>
        )}
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <div>
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {title}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function ConsultationBlock({ consultation: c }) {
  const v = c.vitals || {};
  const vitalBits = [
    v.bp_systolic && `BP ${v.bp_systolic}/${v.bp_diastolic}`,
    v.pulse_rate && `Pulse ${v.pulse_rate}`,
    v.weight && `${v.weight} kg`,
    v.bmi && `BMI ${v.bmi}`,
    v.skin_type,
  ].filter(Boolean);

  return (
    <Block icon={Stethoscope} title="Clinical notes">
      <div className="space-y-2 rounded-lg bg-cream/50 px-3 py-2.5 text-sm">
        {c.chief_complaint && <Line label="Complaints" value={c.chief_complaint} />}
        {c.clinical_notes && <Line label="Observations" value={c.clinical_notes} />}
        {c.diagnoses?.length > 0 && (
          <Line label="Diagnoses" value={c.diagnoses.map((d) =>
            d.details ? `${d.condition} — ${d.details}` : d.condition).join("; ")} />
        )}
        {c.advice && <Line label="Advice" value={c.advice} />}
        {vitalBits.length > 0 && <Line label="Vitals" value={vitalBits.join(" · ")} />}
        {c.prescription_items?.length > 0 && (
          <div className="pt-1">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <Pill className="h-3 w-3" /> Prescription
            </p>
            <div className="space-y-0.5">
              {c.prescription_items.map((p, i) => (
                <p key={i} className="text-xs text-ink">
                  {p.form && <span className="text-muted-foreground">{p.form} </span>}
                  <span className="font-medium">{p.drug_name}</span>
                  {[p.dosage, p.frequency, p.intake, p.duration].filter(Boolean).length > 0 && (
                    <span className="text-muted-foreground">
                      {" — "}{[p.dosage, p.frequency, p.intake, p.duration].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  {p.quantity != null && (
                    <span className="text-muted-foreground">
                      {" · "}<b className="text-ink">{p.quantity}</b>
                      {p.unit ? ` ${p.unit}${p.quantity === 1 ? "" : "s"}` : ""}
                    </span>
                  )}
                  {/* Whether the clinic handed it over is the line an inventory
                      count will be reconciled against, so it shows on the chart
                      rather than living only in the edit form. */}
                  {p.dispense && (
                    <span className="ml-1 rounded bg-navy/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-navy">
                      {p.dispensed_at ? "Dispensed" : "From stock"}
                    </span>
                  )}
                  {p.instructions && <span className="text-muted-foreground"> ({p.instructions})</span>}
                </p>
              ))}
            </div>
            {c.prescription_notes && (
              <p className="mt-1 text-xs italic text-muted-foreground">{c.prescription_notes}</p>
            )}
          </div>
        )}
        {c.documents?.length > 0 && <FileStrip files={c.documents} />}
      </div>
    </Block>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex gap-3">
      <span className="w-24 shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 text-sm text-ink">{value}</span>
    </div>
  );
}

/* The chart's read-only view of the same rows the editor writes: Ray's
 * columns, so the number the patient was quoted is read back in the shape it
 * was entered — unit × cost, what came off, what is left to pay. */
function ItemTable({ title, items, onRemove, onComplete, onEdit, action, estimate }) {
  const cost = items.reduce((s, i) => s + (i.amount || 0), 0);
  const discount = items.reduce((s, i) => s + (i.discount || 0), 0);
  const total = items.reduce((s, i) => s + (i.total || 0), 0);
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <ClipboardList className="h-3.5 w-3.5" /> {title}
        </p>
        <div className="flex items-center gap-2">
          {onEdit && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit}
                    data-testid="edit-items-btn">
              Edit
            </Button>
          )}
          {action}
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-navy/10">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5 text-left font-semibold">Procedure</th>
              <th className="px-3 py-1.5 text-right font-semibold">Unit * Cost(₹)</th>
              <th className="px-3 py-1.5 text-right font-semibold">Discount(₹)</th>
              <th className="px-3 py-1.5 text-right font-semibold">Total(₹)</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-navy/5">
            {items.map((i) => (
              <tr key={i.id} data-testid="visit-item-row">
                <td className="px-3 py-1.5">
                  <span className="text-ink">{i.name}</span>
                  {i.invoice_id && (
                    <span className="ml-1.5 rounded bg-cyan-100 px-1 py-px text-[9px] font-semibold uppercase text-cyan-800">
                      Invoiced
                    </span>
                  )}
                  {i.tax_percent > 0 && (
                    <span className="ml-1.5 text-[11px] text-muted-foreground">
                      +{i.tax_percent}% tax
                    </span>
                  )}
                  {i.notes && <p className="text-xs text-muted-foreground">{i.notes}</p>}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right text-muted-foreground">
                  {i.quantity || 1} × {amt(unitRate(i))}
                </td>
                <td className="px-3 py-1.5 text-right text-muted-foreground">
                  {i.discount ? amt(i.discount) : "—"}
                </td>
                <td className="px-3 py-1.5 text-right font-medium text-ink">{amt(i.total)}</td>
                <td className="whitespace-nowrap px-1 py-1.5">
                  {!i.invoice_id && (
                    <div className="flex items-center gap-0.5">
                      {onComplete && (
                        <button onClick={() => onComplete(i.id)} title="Mark as done"
                          className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-navy hover:bg-navy/5">
                          Mark done
                        </button>
                      )}
                      <button onClick={() => onRemove(i.id)} title="Remove"
                        className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            <tr className="bg-cream/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <td className="px-3 py-1.5 font-semibold">
                {estimate ? "Estimated total" : "Total"}
              </td>
              <td className="px-3 py-1.5 text-right font-semibold">{amt(cost)}</td>
              <td className="px-3 py-1.5 text-right font-semibold">{amt(discount)}</td>
              <td className="px-3 py-1.5 text-right text-sm font-semibold text-navy">{amt(total)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// What one unit cost. Ray-imported rows and anything saved before `rate` was
// stored still have the line's gross, so divide rather than show a blank.
function unitRate(i) {
  if (i.rate != null) return i.rate;
  return (i.amount || 0) / Math.max(i.quantity || 1, 1);
}

function InvoiceRow({ invoice: inv, onPaid }) {
  const [collecting, setCollecting] = useState(false);
  const tone = inv.status === "paid" ? "bg-green-100 text-green-800"
    : inv.status === "partial" ? "bg-amber-100 text-amber-800"
    : inv.status === "overdue" ? "bg-red-100 text-red-700"
    : "bg-navy/10 text-navy";
  return (
    <div className="rounded-lg border border-navy/10 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-semibold text-navy">{inv.invoice_number}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${tone}`}>
            {inv.status}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span>
            <span className="text-muted-foreground">Billed </span>
            <Money value={inv.grand_total} className="font-medium text-ink" />
            <span className="text-muted-foreground"> · Paid </span>
            <Money value={inv.amount_paid} className="font-medium text-green-700" />
            {inv.amount_due > 0 && (
              <>
                <span className="text-muted-foreground"> · Due </span>
                <Money value={inv.amount_due} className="font-semibold text-red-600" />
              </>
            )}
          </span>
          {/* Take the money where the bill already is. Collecting against this
              invoice used to mean leaving the patient's record for the Invoices
              page, finding the same row again and coming back — with the
              patient still standing at the desk. */}
          {onPaid && inv.amount_due > 0 && inv.status !== "cancelled" && (
            <Button size="sm" className="h-7 bg-green-700 text-xs text-white hover:bg-green-800"
                    onClick={() => setCollecting(true)} data-testid="collect-payment-btn">
              <IndianRupee className="mr-1 h-3 w-3" /> Collect
            </Button>
          )}
        </div>
      </div>

      {collecting && (
        <CollectPaymentDialog invoice={inv} onClose={() => setCollecting(false)}
                              onDone={() => { setCollecting(false); onPaid(); }} />
      )}
      <p className="mt-0.5 truncate text-xs text-muted-foreground">
        {(inv.items || []).map((i) => i.description).join(", ")}
      </p>
      {inv.payments?.length > 0 && (
        <div className="mt-1.5 space-y-0.5 border-t border-navy/5 pt-1.5">
          {inv.payments.map((p) => (
            <p key={p.id} className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="font-mono">{p.receipt_number || "Receipt"}</span>
              <span>
                <Money value={p.amount} className="font-medium text-ink" />
                <span className="capitalize"> · {p.method?.replace("_", " ")}</span>
                <span> · {formatDate(p.created_at)}</span>
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* Reception's builtins. The backend accepts these regardless of what the clinic
 * has configured, so offering them is never a way to fail a save; anything the
 * clinic added in settings is merged on top.
 *
 * Merged CAREFULLY, though: the builtins are slugs ("bank_transfer") and the
 * configured modes are display text ("Bank Transfer"), so a plain union lists
 * both and asks reception which "Cash" they meant. The clinic's own spelling
 * wins and the builtins only fill the gaps. */
const PAYMENT_METHODS = ["cash", "card", "upi", "bank_transfer", "cheque", "other"];
const methodKey = (s) => String(s).toLowerCase().replace(/[\s_]/g, "");

/* Recording the payment against an invoice, from the chart.
 *
 * The same POST /payments the Invoices page uses — one money path, one receipt
 * series, one audit line. What is different is only where it is reachable
 * from. */
function CollectPaymentDialog({ invoice, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [modes, setModes] = useState([]);
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    // Prefilled with the balance: settling the bill is what almost every
    // payment is, and a part payment is the one worth retyping.
    setAmount(String(invoice.amount_due ?? ""));
    setMethod("cash"); setReference("");
    api.get("/payment-modes").then((r) => setModes(r.data || [])).catch(() => {});
  }, [invoice]);

  const options = useMemo(() => {
    const configured = modes.map((m) => m.name).filter(Boolean);
    const seen = new Set(configured.map(methodKey));
    return [...configured, ...PAYMENT_METHODS.filter((m) => !seen.has(methodKey(m)))];
  }, [modes]);

  // Re-point the selection at the clinic's spelling once the modes arrive: a
  // Select whose value ("cash") matches no item ("Cash") renders blank, and a
  // blank method box reads as a form that failed to load.
  useEffect(() => {
    if (options.includes(method)) return;
    setMethod(options.find((o) => methodKey(o) === "cash") || options[0] || "cash");
  }, [options]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!invoice) return null;

  const paying = Number(amount);
  const extra = round2(paying - (invoice.amount_due || 0));

  const submit = async () => {
    if (!(paying > 0)) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/payments", {
        invoice_id: invoice.id, amount: paying, method, reference,
      });
      toast.success(`${data.receipt_number} — ${formatCurrency(paying)} received`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Record payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-cream/60 px-3 py-2">
            <p className="font-mono text-xs font-semibold text-navy">{invoice.invoice_number}</p>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(invoice.grand_total)} billed ·{" "}
              <b className="text-red-600">{formatCurrency(invoice.amount_due)} due</b>
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Amount</label>
            <Input className="mt-0.5" type="number" min="0" value={amount}
                   data-testid="payment-amount"
                   onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((m) => (
                  <SelectItem key={m} value={m} className="capitalize">
                    {m.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Reference (txn / cheque no.)" value={reference}
                 onChange={(e) => setReference(e.target.value)} />
          {/* Overpaying is allowed — reception rounds up all day — but the
              excess settles this invoice and stops there. Credit for a LATER
              bill has to be taken as an advance, so say so before the money is
              in rather than when someone comes looking for it. */}
          {extra > 0 && (
            <p className="text-xs text-amber-700">
              {formatCurrency(extra)} over the balance. It is recorded as collected
              but not held as credit — take the extra as an advance if it is meant
              for a later bill.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !(paying > 0)}
                  className="bg-green-700 text-white hover:bg-green-800"
                  data-testid="payment-submit">
            {saving ? "Recording…" : `Take ${formatCurrency(paying > 0 ? paying : 0)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── Billing tab ─────────────────────────── */

// Giving money back out of the drawer — mirrors the refund endpoint's roles.
const REFUND_ROLES = ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"];
const printAdvance = (id) => window.open(`${api.defaults.baseURL}/advances/${id}/pdf`, "_blank");

export function BillingTab({ patientId, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [visits, setVisits] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loose, setLoose] = useState([]);
  const [credit, setCredit] = useState(null);
  const [takingAdvance, setTakingAdvance] = useState(false);

  const load = useCallback(() => {
    api.get(`/patients/${patientId}/timeline`).then((r) => setVisits(r.data.visits || [])).catch(() => {});
    // Invoices raised outside a visit (package instalments) have no
    // appointment_id, so the timeline cannot show them. They still have to be
    // on the billing page or the patient's money does not add up.
    api.get("/invoices", { params: { patient_id: patientId, limit: 200 } })
      .then((r) => setLoose(r.data.items || [])).catch(() => {});
    api.get(`/patients/${patientId}/credit`).then((r) => setCredit(r.data)).catch(() => {});
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  // Money moving here changes the header's lifetime figures too, and a stale
  // "collected" next to a receipt just printed is the one number reception
  // will not believe.
  const reload = () => { load(); onChanged?.(); };

  if (!visits) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  const onVisit = new Set(visits.flatMap((v) => (v.invoices || []).map((i) => i.id)));
  const unlinked = loose.filter((i) => !onVisit.has(i.id));
  const billed = visits.filter((v) => v.invoices?.length);

  return (
    <div className="space-y-4">
      {/* Credit on account. Money taken before there was an invoice to put it
          against — a deposit, or change left on account — has to be visible
          here or the till and the ledger tell different stories. */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <Wallet className="h-5 w-5 text-gold" />
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Credit on account</p>
            <p className="font-display text-xl font-semibold text-navy" data-testid="patient-credit">
              {formatCurrency(credit?.balance || 0)}
            </p>
            {credit?.received > 0 && (
              <p className="text-xs text-muted-foreground">
                {formatCurrency(credit.received)} taken · {formatCurrency(credit.applied)} used
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {/* Billing used to be reachable only through Charting or Checkout,
              so this tab showed the advance and nothing to act on. */}
          <Button size="sm" className="bg-navy text-white hover:bg-navy-hover"
                  onClick={() => navigate(`/invoices/create?patient_id=${patientId}`)}
                  data-testid="new-invoice-btn">
            <Plus className="mr-1 h-3.5 w-3.5" /> New invoice
          </Button>
          <Button size="sm" variant="outline" onClick={() => setTakingAdvance(true)}
                  data-testid="take-advance-btn">
            <Plus className="mr-1 h-3.5 w-3.5" /> Take advance
          </Button>
          {credit?.balance > 0 && REFUND_ROLES.includes(user?.role) && (
            <Button size="sm" variant="outline" onClick={() => setRefunding(true)}
                    className="border-red-200 text-red-700 hover:bg-red-50" data-testid="refund-advance-btn">
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Refund
            </Button>
          )}
          {credit?.balance > 0 && unlinked.concat(billed.flatMap((v) => v.invoices)).some((i) => i.amount_due > 0) && (
            <ApplyCreditButton patientId={patientId} credit={credit}
              invoices={[...billed.flatMap((v) => v.invoices), ...unlinked].filter((i) => i.amount_due > 0)}
              onDone={reload} />
          )}
        </div>
      </div>

      {credit?.entries?.length > 0 && (
        <div className="-mt-2 rounded-xl border border-navy/10 bg-white px-4 py-2 shadow-sm">
          <button type="button" onClick={() => setShowHistory((v) => !v)}
                  className="flex w-full items-center justify-between py-1 text-xs font-medium text-muted-foreground">
            Advance history ({credit.entries.length})
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showHistory ? "rotate-180" : ""}`} />
          </button>
          {showHistory && (
            <div className="divide-y divide-navy/5" data-testid="advance-history">
              {credit.entries.map((e) => {
                const kind = e.kind === "applied" ? "Used on invoice" : e.kind === "refunded" ? "Refunded" : "Advance taken";
                const sign = e.kind === "received" || !e.kind ? "+" : "−";
                return (
                  <div key={e.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="text-ink">{kind}{e.method && e.kind !== "applied" ? ` · ${e.method}` : ""}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {formatDate(e.created_at)}{e.receipt_number ? ` · ${e.receipt_number}` : ""}
                        {e.notes ? ` · ${e.notes}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={sign === "+" ? "font-medium text-green-700" : "font-medium text-ink"}>
                        {sign}{formatCurrency(Math.abs(e.amount || 0))}
                      </span>
                      {e.kind !== "applied" && (
                        <button type="button" title="Print receipt" onClick={() => printAdvance(e.id)}
                                className="rounded p-1 text-navy hover:bg-navy/5">
                          <Printer className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <AdvanceDialog open={takingAdvance} patientId={patientId}
                     onClose={() => setTakingAdvance(false)}
                     onSaved={() => { setTakingAdvance(false); reload(); }} />
      <RefundDialog open={refunding} patientId={patientId} balance={credit?.balance || 0}
                    onClose={() => setRefunding(false)}
                    onSaved={() => { setRefunding(false); reload(); }} />

      {billed.map((v) => (
        <div key={v.id} className="rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
          <p className="mb-2 text-sm font-semibold text-ink">
            {formatDate(v.scheduled_at)} · {v.service || "Appointment"}
            {v.doctor_name ? ` with ${v.doctor_name}` : ""}
          </p>
          <div className="space-y-2">
            {v.invoices.map((inv) => <InvoiceRow key={inv.id} invoice={inv} onPaid={reload} />)}
          </div>
        </div>
      ))}

      {unlinked.length > 0 && (
        <div className="rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
          <p className="mb-2 text-sm font-semibold text-ink">Package instalments &amp; other invoices</p>
          <div className="space-y-2">
            {unlinked.map((inv) => <InvoiceRow key={inv.id} invoice={inv} onPaid={reload} />)}
          </div>
        </div>
      )}

      {!billed.length && !unlinked.length && (
        <p className="py-10 text-center text-sm text-muted-foreground">Nothing billed yet.</p>
      )}
    </div>
  );
}

/* Hand unused advance back. Only what is still on account can go back — money
 * already spent on an invoice is a payment, not credit. */
function RefundDialog({ open, patientId, balance, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [modes, setModes] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount(String(balance || "")); setReason(""); setMethod("cash");
    api.get("/payment-modes").then((r) => setModes(r.data || [])).catch(() => {});
  }, [open, balance]);

  const save = async () => {
    const n = Number(amount);
    if (!(n > 0)) { toast.error("Enter an amount"); return; }
    if (n > balance) { toast.error(`Only ${formatCurrency(balance)} can be refunded`); return; }
    if (!reason.trim()) { toast.error("Give a reason for the refund"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/patients/${patientId}/credit/refund`, {
        amount: n, method, reason: reason.trim(),
      });
      toast.success(`${r.data.receipt_number} — refunded ${formatCurrency(n)}`, {
        duration: 10000,
        action: { label: "Print voucher", onClick: () => printAdvance(r.data.id) },
      });
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-navy">Refund advance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {formatCurrency(balance)} unused advance on account.
          </p>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Amount to refund</label>
            <Input className="mt-0.5" type="number" value={amount} max={balance}
                   onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Paid back by</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(modes.length ? modes.map((m) => m.name) : ["cash", "card", "upi"]).map((m) => (
                  <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Reason (required)" value={reason}
                 onChange={(e) => setReason(e.target.value)} data-testid="refund-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-red-600 text-white hover:bg-red-700">
            {saving ? "Saving…" : "Refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdvanceDialog({ open, patientId, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [modes, setModes] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount(""); setNotes(""); setMethod("cash");
    api.get("/payment-modes").then((r) => setModes(r.data || [])).catch(() => {});
  }, [open]);

  const save = async () => {
    if (!(Number(amount) > 0)) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      const r = await api.post("/advances", {
        patient_id: patientId, amount: Number(amount), method, notes,
      });
      toast.success(`${r.data.receipt_number} — ${formatCurrency(r.data.balance)} on account`, {
        duration: 10000,
        action: { label: "Print receipt", onClick: () => printAdvance(r.data.id) },
      });
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-navy">Take an advance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Amount</label>
            <Input className="mt-0.5" type="number" value={amount}
                   onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(modes.length ? modes.map((m) => m.name) : ["cash", "card", "upi"]).map((m) => (
                  <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Note" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <p className="text-xs text-muted-foreground">
            Held as credit against this patient and spent on any invoice later.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Saving…" : "Take payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplyCreditButton({ patientId, credit, invoices, onDone }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  const totalPicked = Object.values(picked).reduce((a, b) => a + (Number(b) || 0), 0);

  const apply = async () => {
    const allocations = Object.entries(picked)
      .filter(([, amt]) => Number(amt) > 0)
      .map(([invoice_id, amount]) => ({ invoice_id, amount: Number(amount) }));
    if (!allocations.length) { toast.error("Pick at least one invoice"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/credit/apply`, { allocations });
      toast.success(`${formatCurrency(totalPicked)} applied`);
      setOpen(false); setPicked({}); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className="bg-gold text-navy hover:bg-gold/90"
              data-testid="apply-credit-btn">
        <IndianRupee className="mr-1 h-3.5 w-3.5" /> Apply credit
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">
              Apply {formatCurrency(credit.balance)} credit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-2 rounded-lg border border-navy/10 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-xs font-semibold text-navy">{inv.invoice_number}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {formatCurrency(inv.amount_due)} due
                  </p>
                </div>
                <Input className="h-8 w-28" type="number" placeholder="0"
                       value={picked[inv.id] ?? ""}
                       onChange={(e) => setPicked((p) => ({ ...p, [inv.id]: e.target.value }))} />
              </div>
            ))}
            <p className={`text-xs ${totalPicked > credit.balance ? "font-semibold text-red-600" : "text-muted-foreground"}`}>
              Allocating {formatCurrency(totalPicked)} of {formatCurrency(credit.balance)}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={apply} disabled={saving || totalPicked > credit.balance || totalPicked <= 0}
                    className="bg-navy text-white hover:bg-navy-hover">
              {saving ? "Applying…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ────────────────────── Medical history rail ────────────────────── */

const HISTORY_FIELDS = [
  { key: "allergies", label: "Allergies", tone: "text-red-700", icon: AlertTriangle },
  { key: "medical_history", label: "Medical history", tone: "text-ink", icon: null },
  { key: "current_medication", label: "Current medication", tone: "text-ink", icon: Pill },
];

export function MedicalHistoryPanel({ patient, onSaved }) {
  const [suggestions, setSuggestions] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/master-data", { params: { field: "medical_history" } })
      .then((r) => setSuggestions((r.data.medical_history || []).map((m) => m.value)))
      .catch(() => {});
  }, []);

  const save = async (key, values) => {
    setBusy(true);
    try {
      await api.put(`/patients/${patient.id}`, { [key]: values });
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const add = (key, value) => {
    const v = (value || "").trim();
    if (!v) return;
    const current = patient[key] || [];
    if (current.includes(v)) return;
    save(key, [...current, v]);
    setDraft((d) => ({ ...d, [key]: "" }));
  };

  const remove = (key, value) => save(key, (patient[key] || []).filter((x) => x !== value));

  const unusedSuggestions = suggestions
    .filter((s) => !(patient.medical_history || []).includes(s)).slice(0, 8);

  return (
    <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm" data-testid="medical-history">
      <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-navy">
        Medical history
      </h3>

      {HISTORY_FIELDS.map(({ key, label, tone, icon: Icon }) => (
        <div key={key} className="mt-4">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            {Icon && <Icon className={`h-3.5 w-3.5 ${tone}`} />} {label}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {(patient[key] || []).length === 0 && (
              <span className="text-xs italic text-muted-foreground">Not recorded</span>
            )}
            {(patient[key] || []).map((v) => (
              <span key={v}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                  key === "allergies" ? "bg-red-50 text-red-700" : "bg-navy/5 text-ink"}`}>
                {v}
                <button onClick={() => remove(key, v)} disabled={busy}
                        className="text-muted-foreground hover:text-red-600">×</button>
              </span>
            ))}
          </div>
          <Input
            className="mt-1.5 h-8 text-xs"
            placeholder={`Add ${label.toLowerCase()}…`}
            value={draft[key] || ""}
            onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(key, draft[key]); } }}
          />
        </div>
      ))}

      {unusedSuggestions.length > 0 && (
        <div className="mt-4 border-t border-navy/10 pt-3">
          <p className="text-xs font-semibold text-muted-foreground">Common</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {unusedSuggestions.map((s) => (
              <button key={s} onClick={() => add("medical_history", s)} disabled={busy}
                className="rounded-full border border-dashed border-navy/20 px-2 py-0.5 text-xs text-muted-foreground hover:border-navy/40 hover:text-navy">
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Create a new chart ─────────────────── */

// Recording a visit that already happened but was never booked — a walk-in
// nobody entered, or a paper note being typed up days later. Records attach to
// an appointment, so one has to exist before there is anywhere to put them;
// this creates that appointment and nothing else.
//
// The status is decided by the date, not by the user: a visit in the past
// happened (visited), one in the future has not (scheduled). Asking staff to
// pick would just be asking them to restate the date they already gave.
function NewChartDialog({ open, patientId, patient, onClose, onCreated }) {
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({ date: "", time: "", doctor_id: "", duration: "15" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const mins = Math.floor(now.getMinutes() / 5) * 5;
    setForm({
      date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`,
      time: `${String(now.getHours()).padStart(2, "0")}:${String(mins).padStart(2, "0")}`,
      doctor_id: "", duration: "15",
    });
    api.get("/users", { params: { role: "doctor" } })
      .then((r) => setDoctors(r.data || [])).catch(() => {});
  }, [open]);

  const save = async () => {
    if (!form.date || !form.time) { toast.error("Pick the date and time of the visit"); return; }
    const when = new Date(`${form.date}T${form.time}:00`);
    if (Number.isNaN(when.getTime())) { toast.error("That date and time is not valid"); return; }
    setSaving(true);
    try {
      const { data: appt } = await api.post("/appointments", {
        patient_id: patientId,
        patient_name: patient?.name || "",
        mobile: patient?.mobile || "",
        branch_id: patient?.branch_id || null,
        doctor_id: form.doctor_id || null,
        scheduled_at: when.toISOString(),
        duration_minutes: Number(form.duration) || 15,
        notes: "Chart created for a visit that was not booked",
      });
      if (when.getTime() <= Date.now()) {
        // Already happened. Mark it visited so it counts in the day's numbers
        // and the reports, exactly as a booked visit would.
        await api.put(`/appointments/${appt.id}`, {
          status: "visited", visit_outcome: "converted",
        });
      }
      toast.success("Chart created — add records to it below");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const isPast = form.date && form.time
    && new Date(`${form.date}T${form.time}:00`).getTime() <= Date.now();

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">When did the visit happen?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_auto_110px] items-end gap-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Visit date</label>
              <Input className="mt-0.5" type="date" value={form.date}
                     data-testid="new-chart-date"
                     onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <span className="pb-2 text-xs text-muted-foreground">at</span>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Time</label>
              <Input className="mt-0.5" type="time" value={form.time}
                     onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Doctor</label>
            <Select value={form.doctor_id || "__none__"}
                    onValueChange={(v) => setForm((f) => ({ ...f, doctor_id: v === "__none__" ? "" : v }))}>
              <SelectTrigger className="mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not recorded</SelectItem>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Length</label>
            <Select value={form.duration} onValueChange={(v) => setForm((f) => ({ ...f, duration: v }))}>
              <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["10", "15", "30", "45", "60"].map((m) => (
                  <SelectItem key={m} value={m}>{m} minutes</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            {isPast
              ? "This will be recorded as a completed visit, so it counts in today's numbers and the reports."
              : "That is in the future, so it will be created as a scheduled appointment instead."}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover"
                  data-testid="new-chart-save">
            {saving ? "Creating…" : "Create chart"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────── Inline record editor ─────────────── */

// One editor for every record type a visit can carry. They differ only in which
// fields they show and where they save to, so a component each would be seven
// copies of the same open/validate/save/refresh dance.
function VisitRecordEditor({ visit, kind, onCancel, onSaved }) {
  const [consult, setConsult] = useState(null);
  const [form, setForm] = useState({});
  const [rows, setRows] = useState([]);
  const [files, setFiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [saving, setSaving] = useState(false);
  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);

  const onConsultation = ["vitals", "notes", "prescription", "files"].includes(kind);

  useEffect(() => {
    if (onConsultation) {
      ensureConsultation(visit).then((c) => {
        setConsult(c);
        setForm({
          ...(c.vitals || {}),
          chief_complaint: c.chief_complaint || "",
          clinical_notes: c.clinical_notes || "",
          advice: c.advice || "",
          diagnoses: (c.diagnoses || []).map((d) => d.condition).join(", "),
          prescription_notes: c.prescription_notes || "",
        });
        if (kind === "prescription") {
          // An already-saved quantity counts as typed: reopening a script and
          // touching the duration must not silently re-estimate it. Rows start
          // EMPTY — Ray adds the first drug from the search box, not from a
          // blank row nobody asked for.
          setRows((c.prescription_items || []).map((r, i) => ({
            ...r, key: `saved-${i}`, qty_touched: r.quantity != null,
          })));
        }
      }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    }

    if (kind === "prescription") api.get("/drugs").then((r) => setCatalog(r.data || [])).catch(() => {});
    if (kind === "lab") setRows([{ name: "", instructions: "" }]);
    if (kind === "consent") {
      api.get("/consent-templates").then((r) => setCatalog(r.data || [])).catch(() => {});
    }
    if (["completed", "planned"].includes(kind)) {
      // Open onto what is already on the visit. The editor owns the whole list,
      // so anything it is not shown it cannot preserve.
      setRows(itemsOfKind(visit, kind).map(procRow));
      api.get("/packages").then((r) => setCatalog(r.data || [])).catch(() => {});
    }
    if (kind === "notes") {
      Promise.all(["complaint", "observation", "diagnosis"].map((f) =>
        api.get("/master-data", { params: { field: f } })
          .then((r) => [f, (r.data[f] || []).map((x) => x.value)]).catch(() => [f, []])))
        .then((pairs) => setSuggestions(Object.fromEntries(pairs)));
    }
  }, [visit, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRow = (i, patch) => setRows((rs) => rs.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));

  // Signature pad. Mouse and touch both, because consent is signed on the
  // reception tablet far more often than with a mouse.
  const penPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: ((t?.clientX ?? e.clientX) - r.left) * (canvasRef.current.width / r.width),
      y: ((t?.clientY ?? e.clientY) - r.top) * (canvasRef.current.height / r.height),
    };
  };
  const penDown = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = penPos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const penMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = penPos(e);
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#1B3A5C";
    ctx.lineTo(x, y); ctx.stroke();
  };
  const penUp = () => { drawing.current = false; };
  const clearPad = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (kind === "vitals") {
        await api.put(`/consultations/${consult.id}`, {
          vitals: {
            weight: num(form.weight), height: num(form.height),
            bp_systolic: num(form.bp_systolic), bp_diastolic: num(form.bp_diastolic),
            pulse_rate: num(form.pulse_rate), temperature: num(form.temperature),
            spo2: num(form.spo2), skin_type: form.skin_type || "",
            vitals_notes: form.vitals_notes || "",
          },
        });
      } else if (kind === "notes") {
        await api.put(`/consultations/${consult.id}`, {
          chief_complaint: form.chief_complaint || "",
          clinical_notes: form.clinical_notes || "",
          advice: form.advice || "",
          diagnoses: splitCsv(form.diagnoses).map((condition, i) =>
            ({ condition, details: "", is_primary: i === 0 })),
        });
      } else if (kind === "prescription") {
        // qty_touched / instr_open / key are UI state ("the doctor typed this
        // number", "the instruction box is open"), not part of the record —
        // stripped rather than left to settle into the database. The two human
        // strings are rendered here, once, so the chart and the PDF cannot
        // disagree about what was written.
        const items = rows.filter((r) => (r.drug_name || "").trim())
          .map(({ qty_touched, instr_open, key, ...item }) => ({
            ...item,
            dosage: renderDosage(item),
            duration: renderDuration(item),
          }));
        if (!items.length) throw new Error("Add at least one drug");
        await api.put(`/consultations/${consult.id}`, {
          prescription_items: items,
          prescription_notes: form.prescription_notes || "",
        });
      } else if (kind === "files") {
        if (!files.length) throw new Error("Choose at least one file");
        for (const f of files) {
          const fd = new FormData();
          fd.append("file", f); fd.append("tag", "during");
          await api.post(`/consultations/${consult.id}/photos`, fd,
                         { headers: { "Content-Type": "multipart/form-data" } });
        }
      } else if (kind === "lab") {
        const tests = rows.filter((r) => (r.name || "").trim());
        if (!tests.length) throw new Error("Add at least one test");
        await api.post("/lab-orders", {
          patient_id: visit.patient_id, appointment_id: visit.id,
          tests, lab_name: form.lab_name || "", notes: form.notes || "",
        });
      } else if (kind === "consent") {
        if (!form.template_id) throw new Error("Pick a consent form");
        const signature = canvasRef.current?.toDataURL("image/png");
        // A blank canvas still yields a perfectly valid data URL, so the only way
        // to tell "unsigned" from "signed" is to render an empty one and compare.
        const blank = document.createElement("canvas");
        blank.width = canvasRef.current.width;
        blank.height = canvasRef.current.height;
        if (signature === blank.toDataURL("image/png")) throw new Error("Please capture a signature");
        await api.post("/consents", {
          template_id: form.template_id, patient_id: visit.patient_id,
          appointment_id: visit.id, signature, signed_by: form.signed_by || "",
        });
      } else {
        // The editor holds the list, so saving is a diff against what was on
        // the visit when it opened: rows taken out are deleted, rows touched
        // are updated, and only genuinely new lines are posted. Sending every
        // row as an insert would duplicate the visit each time it is reopened.
        const was = itemsOfKind(visit, kind);
        const live = rows.filter((r) => (r.name || "").trim());
        if (!live.length && !was.length) throw new Error("Add at least one procedure");
        // A saved line whose name was cleared is someone half-way through
        // retyping it, not someone deleting it — and the diff below would read
        // it as a deletion and take the price and notes with it.
        if (rows.some((r) => r.id && !(r.name || "").trim())) {
          throw new Error("Give every procedure a name, or remove the line with ✕");
        }

        const kept = new Set(live.filter((r) => r.id).map((r) => r.id));
        for (const old of was) {
          if (!kept.has(old.id) && !old.invoice_id) await api.delete(`/visit-items/${old.id}`);
        }
        for (const r of live) {
          if (r.invoice_id) continue;          // billed: the invoice owns it now
          const body = procBody(r);
          if (r.id) {
            if (procSignature(r) !== r.saved) await api.put(`/visit-items/${r.id}`, body);
          } else {
            await api.post(`/appointments/${visit.id}/items`,
                           { kind, package_id: r.package_id || null, ...body });
          }
        }
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response ? formatApiError(e.response?.data?.detail) : e.message);
    } finally { setSaving(false); }
  };

  const title = {
    vitals: "Vital signs", notes: "Clinical notes", prescription: "Prescription",
    files: "Files", lab: "Lab order", planned: "Treatment plan",
    completed: "Completed procedures", consent: "Consent",
  }[kind];

  if (onConsultation && !consult) {
    return <p className="py-4 text-center text-sm text-muted-foreground">Opening the record…</p>;
  }

  return (
    <EditorPanel title={title} onCancel={onCancel} onSave={save} saving={saving}>
      {kind === "vitals" && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[["weight", "Weight (kg)"], ["height", "Height (cm)"], ["bp_systolic", "BP systolic"],
            ["bp_diastolic", "BP diastolic"], ["pulse_rate", "Pulse"], ["temperature", "Temp (°C)"],
            ["spo2", "SpO₂ (%)"], ["skin_type", "Skin type"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs font-medium text-muted-foreground">{l}</label>
              <Input className="mt-0.5 h-8" type={k === "skin_type" ? "text" : "number"}
                     value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} />
            </div>
          ))}
        </div>
      )}

      {kind === "notes" && (
        <div className="space-y-2.5">
          {[["chief_complaint", "Complaints", "complaint"],
            ["clinical_notes", "Observations", "observation"],
            ["diagnoses", "Diagnoses (comma separated)", "diagnosis"],
            ["advice", "Advice", null]].map(([k, l, sug]) => (
            <div key={k}>
              <label className="text-xs font-medium text-muted-foreground">{l}</label>
              <Textarea className="mt-0.5 bg-white" rows={2} value={form[k] || ""}
                        onChange={(e) => set(k, e.target.value)} />
              {sug && suggestions[sug]?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {suggestions[sug].slice(0, 6).map((s) => (
                    <button key={s} type="button"
                      onClick={() => set(k, form[k] ? `${form[k]}, ${s}` : s)}
                      className="rounded-full border border-dashed border-navy/20 px-2 py-0.5 text-[11px] text-muted-foreground hover:border-navy/40 hover:text-navy">
                      + {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {kind === "prescription" && (
        // Ray's four columns, because they are the four decisions a doctor
        // makes per drug and they make them in this order: what, how much and
        // how often, with or without food, for how long.
        <div className="rx-rows space-y-1">
          <div className="rx-head border-b border-navy/10 px-1 pb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            <span>Drug</span><span>Dosage &amp; Frequency</span><span>Intake</span><span>Duration</span><span className="w-5" />
          </div>

          {rows.map((r, i) => (
            <DrugRow key={r.key ?? i} row={r} catalog={catalog}
                     onChange={(patch) => setRow(i, patch)}
                     onRemove={() => setRows((rs) => rs.filter((_, ix) => ix !== i))} />
          ))}

          {/* Ray adds drugs from a search box under the list rather than an
              empty row: you look the drug up, and the row appears already
              carrying its form, unit and standing instructions. */}
          <div className="pt-2">
            <DrugSearch catalog={catalog}
                        onPick={(drug) => setRows((rs) => [...rs, drugRow(drug)])}
                        onCreated={(drug) => {
                          setCatalog((c) => [...c, drug]);
                          setRows((rs) => [...rs, drugRow(drug)]);
                        }} />
          </div>

          <div className="border-t border-navy/10 pt-3">
            <Textarea rows={2} className="bg-white" placeholder="Add Instruction…"
                      value={form.prescription_notes || ""}
                      onChange={(e) => set("prescription_notes", e.target.value)} />
            <p className="mt-1 px-1 text-[11px] text-muted-foreground">
              Applies to the whole prescription. Per-drug directions go on the drug.
            </p>
          </div>
        </div>
      )}

      {kind === "files" && (
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-navy/20 bg-white py-6 text-sm font-medium text-navy hover:bg-cream/60">
          <Paperclip className="h-5 w-5" />
          {files.length ? `${files.length} file(s) selected` : "Choose photos or documents"}
          <input type="file" multiple className="hidden" accept="image/*,.pdf"
                 onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </label>
      )}

      {kind === "lab" && (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input className="h-8 bg-white" placeholder="Test name" value={r.name}
                     onChange={(e) => setRow(i, { name: e.target.value })} />
              <Input className="h-8 bg-white" placeholder="Instructions (e.g. fasting)"
                     value={r.instructions}
                     onChange={(e) => setRow(i, { instructions: e.target.value })} />
              <button onClick={() => setRows((rs) => rs.filter((_, ix) => ix !== i))}
                      disabled={rows.length === 1}
                      className="rounded p-1 text-muted-foreground hover:text-red-600 disabled:opacity-30">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" variant="outline"
                    onClick={() => setRows((rs) => [...rs, { name: "", instructions: "" }])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add test
            </Button>
            <Input className="h-8 max-w-[200px] bg-white" placeholder="Lab name"
                   value={form.lab_name || ""} onChange={(e) => set("lab_name", e.target.value)} />
          </div>
        </div>
      )}

      {["completed", "planned"].includes(kind) && (
        <ProcedureRows rows={rows} catalog={catalog} estimate={kind === "planned"}
                       onChangeRow={(i, patch) => setRow(i, patch)}
                       onRemoveRow={(i) => setRows((rs) => rs.filter((_, ix) => ix !== i))}
                       onAddRow={(seed) => setRows((rs) => [...rs, procRow(seed)])} />
      )}

      {kind === "consent" && (
        <div className="space-y-2.5">
          <div className="grid gap-2 md:grid-cols-2">
            <Select value={form.template_id || ""} onValueChange={(v) => set("template_id", v)}>
              <SelectTrigger className="h-8 bg-white"><SelectValue placeholder="Choose a consent form" /></SelectTrigger>
              <SelectContent>
                {catalog.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className="h-8 bg-white" placeholder="Signed by (blank = the patient)"
                   value={form.signed_by || ""} onChange={(e) => set("signed_by", e.target.value)} />
          </div>
          {catalog.find((t) => t.id === form.template_id)?.body && (
            <div className="max-h-40 overflow-y-auto rounded-lg bg-white p-3 text-xs leading-relaxed text-ink">
              {catalog.find((t) => t.id === form.template_id).body}
            </div>
          )}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Signature</label>
              <button type="button" onClick={clearPad} className="text-xs text-navy hover:underline">Clear</button>
            </div>
            <canvas
              ref={canvasRef} width={640} height={160}
              onMouseDown={penDown} onMouseMove={penMove} onMouseUp={penUp} onMouseLeave={penUp}
              onTouchStart={penDown} onTouchMove={penMove} onTouchEnd={penUp}
              className="w-full touch-none cursor-crosshair rounded-lg border border-dashed border-navy/30 bg-white"
            />
          </div>
        </div>
      )}
    </EditorPanel>
  );
}

/* ───────── Completed procedures / treatment plan: Ray's table ─────────
 *
 * A visit is rarely one procedure. The form this replaces took exactly one —
 * name, qty, rate, discount, tax — and then greyed its own menu entry out, so
 * the second thing done that afternoon had nowhere to go and the first could
 * never be corrected. Ray keeps the whole visit on screen as a table you add
 * lines to, with the running total under it, because the number being built up
 * is the number about to be read out at the desk.
 *
 * Two differences from Ray, both deliberate:
 *   · Discount takes ₹ or %, and % is resolved to rupees before it is saved.
 *     The stored figure is what came off the bill; a percentage is how it was
 *     decided, and re-deriving it later from a changed rate would be a lie.
 *   · Tax lives in the notes drawer rather than in a fifth column. Ray has no
 *     tax on this screen, but our invoice does, so the line total has to carry
 *     it or the chart and the bill disagree.
 */
function ProcedureRows({ rows, catalog, estimate, onChangeRow, onRemoveRow, onAddRow }) {
  const sums = rows.reduce((s, r) => {
    const m = lineMoney(r);
    return { cost: s.cost + m.gross, discount: s.discount + m.discount,
             tax: s.tax + m.tax, total: s.total + m.total };
  }, { cost: 0, discount: 0, tax: 0, total: 0 });

  return (
    <div className="proc-rows">
      <div className="proc-head border-b border-navy/10 px-1 pb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <span>Procedure</span><span>Unit * Cost(₹)</span><span>Discount</span>
        <span className="proc-amount">Total(₹)</span><span className="w-5" />
      </div>

      {rows.map((r, i) => (
        <ProcedureRow key={r.key} row={r}
                      onChange={(patch) => onChangeRow(i, patch)}
                      onRemove={() => onRemoveRow(i)} />
      ))}

      {!rows.length && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Nothing on this visit yet — find the procedure below.
        </p>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4 border-t border-navy/10 pt-3">
        <ProcedureSearch catalog={catalog} onPick={onAddRow} />
        <div className="flex flex-wrap items-end gap-5">
          <Figure label="Total cost(₹)" value={sums.cost} />
          <Figure label="Total discount(₹)" value={sums.discount} />
          {sums.tax > 0 && <Figure label="Tax(₹)" value={sums.tax} />}
          <Figure label={estimate ? "Estimated total(₹)" : "Grand total(₹)"} value={sums.total} strong />
        </div>
      </div>
    </div>
  );
}

function Figure({ label, value, strong }) {
  return (
    <div className="text-right">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={strong ? "font-display text-base font-semibold text-navy" : "text-sm text-ink"}
         data-testid={`proc-${strong ? "grand-total" : "figure"}`}>
        {amt(value)}
      </p>
    </div>
  );
}

function ProcedureRow({ row: r, onChange, onRemove }) {
  const m = lineMoney(r);
  // An invoiced line belongs to the invoice — the backend refuses to touch it,
  // so the row is shown rather than offered for editing. A read-only box that
  // still looks like a box invites the typing it will then swallow, so locked
  // fields drop the white fill that says "write here".
  const locked = !!r.invoice_id;
  const box = locked ? "bg-cream/40 text-muted-foreground" : "bg-white";

  return (
    <div className="proc-row border-b border-navy/5 py-2.5 last:border-b-0" data-testid="procedure-row">
      {/* PROCEDURE */}
      <div className="min-w-0">
        <Input value={r.name || ""} readOnly={locked} placeholder="Procedure name"
               onChange={(e) => onChange({ name: e.target.value })}
               className="h-7 border-0 bg-transparent px-0 text-sm text-ink shadow-none focus-visible:ring-0" />
        <div className="flex flex-wrap items-center gap-2">
          {locked && (
            <span className="rounded bg-cyan-100 px-1 py-px text-[9px] font-semibold uppercase text-cyan-800">
              Invoiced
            </span>
          )}
          {!r.notes_open && !locked && (
            <button type="button" onClick={() => onChange({ notes_open: true })}
                    className="text-[11px] text-sky-600 hover:underline">add notes</button>
          )}
          {!r.notes_open && Number(r.tax_percent) > 0 && (
            <span className="text-[11px] text-muted-foreground">+{Number(r.tax_percent)}% tax</span>
          )}
          {!r.notes_open && locked && r.notes && (
            <span className="truncate text-[11px] text-muted-foreground">{r.notes}</span>
          )}
        </div>
        {r.notes_open && (
          <div className="mt-1 flex items-center gap-2">
            <Input className={`h-8 min-w-0 flex-1 text-xs ${box}`} placeholder="Notes"
                   value={r.notes || ""} readOnly={locked}
                   onChange={(e) => onChange({ notes: e.target.value })} />
            <div className="flex shrink-0 items-center gap-1">
              <Input className={`h-8 w-14 text-center text-xs ${box}`} type="number" min="0" placeholder="0"
                     value={r.tax_percent ?? ""} readOnly={locked}
                     onChange={(e) => onChange({ tax_percent: e.target.value })} />
              <span className="text-[11px] text-muted-foreground">% tax</span>
            </div>
          </div>
        )}
      </div>

      {/* UNIT * COST */}
      <div className="min-w-0">
        <span className="proc-label">Unit * Cost(₹)</span>
        <div className="flex items-center gap-1.5">
          <Input className={`h-8 w-14 shrink-0 text-center ${box}`} type="number" min="1" step="1"
                 value={r.quantity ?? 1} readOnly={locked}
                 onChange={(e) => onChange({ quantity: e.target.value })} />
          <Input className={`h-8 min-w-0 flex-1 ${box}`} type="number" min="0" placeholder="0"
                 value={r.rate ?? ""} readOnly={locked}
                 onChange={(e) => onChange({ rate: e.target.value })} />
        </div>
      </div>

      {/* DISCOUNT */}
      <div className="min-w-0">
        <span className="proc-label">Discount</span>
        <div className="flex items-center gap-1.5">
          <Input className={`h-8 min-w-0 flex-1 ${box}`} type="number" min="0" placeholder="0"
                 value={r.discount ?? ""} readOnly={locked}
                 onChange={(e) => onChange({ discount: e.target.value })} />
          <Select value={r.discount_type || "amount"} disabled={locked}
                  onValueChange={(v) => onChange({ discount_type: v })}>
            <SelectTrigger className={`h-8 w-16 shrink-0 text-xs ${box}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="amount">₹</SelectItem>
              <SelectItem value="percent">%</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {r.discount_type === "percent" && m.discount > 0 && (
          <p className="mt-0.5 text-[11px] text-muted-foreground">= ₹{amt(m.discount)}</p>
        )}
      </div>

      {/* TOTAL */}
      <div className="proc-amount min-w-0">
        <span className="proc-label">Total(₹)</span>
        <p className="pt-1.5 text-sm font-medium text-ink" data-testid="procedure-total">{amt(m.total)}</p>
      </div>

      {locked ? <span className="w-5" /> : (
        <button onClick={onRemove} title="Remove"
                className="justify-self-start rounded p-1 text-red-500 hover:bg-red-50">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// Row identity has to survive re-renders and outlive the id a saved row has,
// because a new row has no id at all and index keys re-use state across
// removals — take one line out and the next inherits its open notes drawer.
let procKey = 0;

/* A row, from a saved visit item, a catalogue package, or a typed-in name.
 * `saved` is the fingerprint it arrived with: the only thing that separates a
 * row worth a PUT from one the user merely looked at. */
function procRow(seed = {}) {
  const row = {
    key: seed.id || `new-${++procKey}`,
    id: seed.id || null,
    invoice_id: seed.invoice_id || null,
    package_id: seed.package_id || "",
    name: seed.name || "",
    quantity: seed.quantity ?? 1,
    // Rows imported from Ray, and anything saved before the unit rate was
    // stored, only have the line's gross. Opening one into a blank cost box
    // and saving would silently price the procedure at zero.
    rate: seed.rate ?? (seed.id ? unitRate(seed) : ""),
    discount: seed.discount || "",
    discount_type: "amount",
    tax_percent: seed.tax_percent ?? "",
    notes: seed.notes || "",
    notes_open: !!seed.notes,
  };
  row.saved = procSignature(row);
  return row;
}

const itemsOfKind = (visit, kind) =>
  (kind === "completed" ? visit.procedures : visit.treatment_plan) || [];

// What the row means to the API. Quantity is floored at 1 because the backend
// rejects less and a blank box should not be a validation error.
function procBody(r) {
  return {
    name: (r.name || "").trim(),
    quantity: Math.max(1, Math.floor(Number(r.quantity) || 1)),
    rate: Number(r.rate) || 0,
    discount: lineMoney(r).discount,
    tax_percent: r.tax_percent === "" || r.tax_percent == null ? 0 : Number(r.tax_percent),
    notes: r.notes || "",
  };
}

const procSignature = (r) => JSON.stringify(procBody(r));

/* ──────────────────── Files on a visit ──────────────────── */

const IMAGE_RE = /\.(jpe?g|png|webp|gif|bmp)$/i;
const isImage = (f) => IMAGE_RE.test(f.name || "") || f.doc_type === "clinical_photo";
const fileUrl = (f, thumb) =>
  `${API_BASE}/documents/${f.id}/file${thumb ? "?thumb=true" : ""}`;

/* Thumbnails, not a count.
 *
 * The chart said "3 file(s) attached", which tells a doctor nothing they need:
 * the whole reason to photograph a lesion is to LOOK at it next to the one from
 * six weeks ago. Ray shows the strip inline, and reading the chart becomes
 * looking at the case rather than clicking through to find out what is in it. */
function FileStrip({ files }) {
  const [viewing, setViewing] = useState(null);   // index into files
  // A file whose bytes are gone renders as a browser's broken-image glyph,
  // which reads as "this page is broken" rather than "this file is missing".
  // It happens for real: records imported ahead of their files, a failed
  // upload, a storage migration mid-flight.
  const [broken, setBroken] = useState(() => new Set());
  const markBroken = (id) => setBroken((b) => new Set(b).add(id));

  return (
    <div className="pt-1.5">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Paperclip className="h-3 w-3" /> Files
      </p>
      <div className="flex flex-wrap gap-2">
        {files.map((f, i) => (
          <button key={f.id} onClick={() => setViewing(i)} title={f.name}
                  data-testid="file-thumb"
                  className="h-16 w-16 overflow-hidden rounded-lg border border-navy/10 bg-cream/60 transition-shadow hover:shadow-md">
            {isImage(f) && !broken.has(f.id) ? (
              <img src={fileUrl(f, true)} alt={f.name} onError={() => markBroken(f.id)}
                   className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-1 text-center text-[9px] text-muted-foreground">
                {broken.has(f.id)
                  ? <><AlertTriangle className="h-4 w-4 text-amber-500" />missing</>
                  : <><FileText className="h-5 w-5" />
                      {(f.name || "").split(".").pop()?.toUpperCase().slice(0, 4)}</>}
              </span>
            )}
          </button>
        ))}
      </div>
      {viewing != null && (
        <FileViewer files={files} index={viewing}
                    onIndex={setViewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

/* The viewer. Zoom and rotate exist because clinical photos arrive as whatever
 * the phone produced — sideways, too dark, framed from a metre away — and the
 * alternative to fixing that on screen is downloading each one. */
function FileViewer({ files, index, onIndex, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [turn, setTurn] = useState(0);
  const file = files[index];

  // Every navigation resets the view: carrying a 4x zoom onto the next photo
  // means landing on someone's cheek with no idea what you are looking at.
  const go = useCallback((next) => {
    if (next < 0 || next >= files.length) return;
    setZoom(1); setTurn(0); onIndex(next);
  }, [files.length, onIndex]);

  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(index + 1);
      if (e.key === "ArrowLeft") go(index - 1);
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [index, go, onClose]);

  const Tool = ({ onClick, title, children, disabled }) => (
    <button onClick={onClick} title={title} disabled={disabled}
            className="rounded border border-white/25 px-2.5 py-1.5 text-white/90 transition-colors hover:bg-white/15 disabled:opacity-30">
      {children}
    </button>
  );

  // Rendered into <body>. `position: fixed` resolves against the nearest
  // ancestor with a transform/filter, not the viewport — and the chart is full
  // of them (transitions, the editor panel). Inside the tree the overlay came
  // out clipped to the visit card. A portal is the only way a full-screen
  // layer is genuinely full-screen.
  return createPortal((
    <div className="fixed inset-0 z-[100] flex flex-col bg-[#0B0B0E]" data-testid="file-viewer"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex items-center justify-between gap-4 px-5 py-3 text-white">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{file.name}</p>
          <p className="text-[11px] text-white/50">
            {file.uploaded_by_name && `${file.uploaded_by_name} · `}
            {formatDate(file.created_at)}
            {file.size ? ` · ${(file.size / 1024).toFixed(0)} KB` : ""}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2 text-sm">
          <Tool onClick={() => go(index - 1)} title="Previous" disabled={index === 0}>‹</Tool>
          <span className="text-white/70">File {index + 1} of {files.length}</span>
          <Tool onClick={() => go(index + 1)} title="Next" disabled={index === files.length - 1}>›</Tool>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <a href={`${API_BASE}/documents/${file.id}/download`}
             className="rounded border border-white/25 px-3 py-1.5 text-xs text-white/90 hover:bg-white/15">
            Download
          </a>
          <Tool onClick={onClose} title="Close (Esc)"><X className="h-4 w-4" /></Tool>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
        {isImage(file) ? (
          <img src={fileUrl(file)} alt={file.name} data-testid="viewer-image"
               style={{ transform: `scale(${zoom}) rotate(${turn}deg)` }}
               className="max-h-full max-w-full object-contain transition-transform" />
        ) : (
          // A PDF report is a file the browser can already render; an iframe
          // beats making the doctor download it to read one line.
          <iframe src={fileUrl(file)} title={file.name}
                  className="h-full w-full rounded bg-white" />
        )}
      </div>

      {isImage(file) && (
        <div className="flex items-center justify-center gap-1.5 pb-5 text-sm">
          <Tool onClick={() => setZoom((z) => Math.min(6, z * 1.25))} title="Zoom in">
            <ZoomIn className="h-4 w-4" />
          </Tool>
          <Tool onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))} title="Zoom out">
            <ZoomOut className="h-4 w-4" />
          </Tool>
          <Tool onClick={() => setTurn((t) => t - 90)} title="Rotate left">
            <RotateCcw className="h-4 w-4" />
          </Tool>
          <Tool onClick={() => setTurn((t) => t + 90)} title="Rotate right">
            <RotateCw className="h-4 w-4" />
          </Tool>
          <Tool onClick={() => { setZoom(1); setTurn(0); }} title="Reset">
            <Maximize className="h-4 w-4" />
          </Tool>
        </div>
      )}
    </div>
  ), document.body);
}

/* ─────────────── Prescription: one drug, Ray's four columns ─────────────── */

// Frequencies Ray offers. Free text is still allowed — the list is a shortcut
// for the ones written twenty times a day, not a whitelist.
const FREQUENCIES = [
  "once daily", "twice daily", "three times a day", "four times a day",
  "every 4 hours", "every 6 hours", "every 8 hours", "every 12 hours",
  "every 2 hours", "every other hour", "every hour",
  "at bedtime", "alternate days", "once a week", "as needed (SOS)", "immediately (STAT)",
];

const INTAKES = ["Before Food", "After Food"];
const DURATION_UNITS = ["day(s)", "week(s)", "month(s)"];

function DrugRow({ row: r, catalog, onChange, onRemove }) {
  // "Custom" swaps the single dose box for morning-afternoon-night, which is
  // how a derm writes a tapering or split dose. The link flips to "Default".
  const custom = r.dose_pattern != null;
  const pattern = (r.dose_pattern || "0-0-0").split("-");
  const drug = catalog.find((x) => x.id === r.drug_id);

  // Every dose/frequency/duration edit re-runs the quantity estimate, so the
  // number inventory will one day subtract stays in step with what was written.
  const edit = (patch) => onChange(withEstimate({ ...r, ...patch }));

  const setSlot = (ix, val) => {
    const next = [...pattern];
    next[ix] = String(Number(val) || 0);
    edit({ dose_pattern: next.join("-") });
  };

  return (
    <div className="rx-row border-b border-navy/5 py-2.5 last:border-b-0">
      {/* DRUG */}
      <div className="min-w-0">
        <p className="text-sm text-ink">
          {r.form && <span className="text-muted-foreground">{r.form} </span>}
          {r.drug_name}
        </p>
        {(r.unit || drug?.stocked) && (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            {r.quantity != null && (
              <span>Qty <b className="text-ink">{r.quantity}</b>{r.unit ? ` ${r.unit}` : ""}</span>
            )}
            {drug?.stocked && (
              <label className="flex cursor-pointer items-center gap-1">
                <input type="checkbox" className="h-3 w-3 accent-[#1B3A5C]"
                       checked={!!r.dispense}
                       onChange={(e) => onChange({ dispense: e.target.checked })} />
                from stock
              </label>
            )}
          </div>
        )}
      </div>

      {/* DOSAGE & FREQUENCY */}
      <div className="min-w-0">
        <span className="rx-label">Dosage &amp; frequency</span>
        <div className="flex items-center gap-1.5">
          {custom ? (
            <div className="flex items-center gap-1">
              {[0, 1, 2].map((ix) => (
                <React.Fragment key={ix}>
                  {ix > 0 && <span className="text-muted-foreground">-</span>}
                  <Input className="h-8 w-11 bg-white px-1 text-center" type="number" min="0"
                         value={pattern[ix] ?? "0"}
                         onChange={(e) => setSlot(ix, e.target.value)} />
                </React.Fragment>
              ))}
            </div>
          ) : (
            <Input className="h-8 w-14 bg-white text-center" type="number" min="0" step="0.5"
                   value={r.dose ?? ""} onChange={(e) => edit({ dose: e.target.value === "" ? null : Number(e.target.value) })} />
          )}
          <span className="shrink-0 text-xs text-muted-foreground">{r.dose_unit || "unit(s)"}</span>
        </div>
        <button type="button"
                onClick={() => edit(custom
                  ? { dose_pattern: null, dose: r.dose ?? 1 }
                  : { dose_pattern: "0-0-0" })}
                className="mt-0.5 text-[11px] text-sky-600 hover:underline">
          {custom ? "Default" : "Custom"}
        </button>
        <div className="mt-1">
          <Suggest value={r.frequency || ""} options={FREQUENCIES} placeholder="Add frequency"
                   onChange={(v) => edit({ frequency: v })} />
        </div>
      </div>

      {/* INTAKE */}
      <div className="min-w-0">
        <span className="rx-label">Intake</span>
        <Select value={r.intake || "__none__"}
                onValueChange={(v) => onChange({ intake: v === "__none__" ? "" : v })}>
          <SelectTrigger className="h-8 bg-white text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Select</SelectItem>
            {INTAKES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        {r.instructions || r.instr_open ? (
          <Input className="mt-1 h-8 bg-white text-xs" placeholder="Add Instruction"
                 value={r.instructions || ""} autoFocus={!!r.instr_open && !r.instructions}
                 onChange={(e) => onChange({ instructions: e.target.value })} />
        ) : (
          <button type="button" onClick={() => onChange({ instr_open: true })}
                  className="mt-1 text-[11px] text-sky-600 hover:underline">+ Add Instruction</button>
        )}
      </div>

      {/* DURATION */}
      <div className="min-w-0">
        <span className="rx-label">Duration</span>
        <div className="flex items-start gap-1.5">
        <Input className="h-8 w-16 bg-white text-center" type="number" min="0"
               value={r.duration_value ?? ""}
               onChange={(e) => edit({ duration_value: e.target.value === "" ? null : Number(e.target.value) })} />
        <Select value={r.duration_unit || "day(s)"}
                onValueChange={(v) => edit({ duration_unit: v })}>
          <SelectTrigger className="h-8 w-[5.5rem] border-0 bg-transparent px-1 text-xs text-sky-600 shadow-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DURATION_UNITS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
      </div>

      <button onClick={onRemove} title="Remove"
              className="justify-self-start rounded p-1 text-red-500 hover:bg-red-50 md:justify-self-center">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// The drug picker under the list. Ray shows strength in the result line, which
// is what tells 5 mg from 10 mg apart when the names are otherwise identical.
function DrugSearch({ catalog, onPick, onCreated }) {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(null);   // the name being created

  return (
    <>
      <Suggest value={q} options={catalog} placeholder="Search drugs" className="w-64"
               onChange={setQ}
               onPick={(d) => { onPick(d); setQ(""); }}
               footer={(close) => (
                 <button type="button"
                         onClick={() => { setAdding(q.trim()); close(); }}
                         className="block w-full px-3 py-1.5 text-left text-xs font-medium text-sky-600 hover:bg-nude/50">
                   Add New Drug
                 </button>
               )} />
      {adding !== null && (
        <NewDrugForm initialName={adding}
                     onCancel={() => setAdding(null)}
                     onCreated={(d) => { setAdding(null); setQ(""); onCreated(d); }} />
      )}
    </>
  );
}

// Adding a drug mid-consult, without leaving the chart for Settings. It asks
// for the inventory fields too — a drug added in a hurry with no unit is
// exactly the row that cannot be counted later.
function NewDrugForm({ initialName, onCancel, onCreated }) {
  const [f, setF] = useState({ name: initialName || "", form: "", strength: "",
                               unit: "", stocked: false, instructions: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { toast.error("A drug name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/drugs", { ...f, active: true });
      toast.success(`${data.name} added to the catalogue`);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-2 rounded-lg border border-navy/15 bg-white p-3">
      <p className="mb-2 text-xs font-semibold text-navy">New drug</p>
      <div className="grid gap-2 md:grid-cols-4">
        <Input className="h-8 md:col-span-2" placeholder="Name" value={f.name}
               onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} />
        <Input className="h-8" placeholder="Form (TABLET)" value={f.form}
               onChange={(e) => setF((s) => ({ ...s, form: e.target.value.toUpperCase() }))} />
        <Input className="h-8" placeholder="Strength" value={f.strength}
               onChange={(e) => setF((s) => ({ ...s, strength: e.target.value }))} />
        <Input className="h-8" placeholder="Unit (tablet)" value={f.unit}
               onChange={(e) => setF((s) => ({ ...s, unit: e.target.value }))} />
        <Input className="h-8 md:col-span-2" placeholder="Default instructions" value={f.instructions}
               onChange={(e) => setF((s) => ({ ...s, instructions: e.target.value }))} />
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" className="h-3.5 w-3.5 accent-[#1B3A5C]" checked={f.stocked}
                 onChange={(e) => setF((s) => ({ ...s, stocked: e.target.checked }))} />
          Dispensed in-house
        </label>
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving}
                className="bg-navy text-white hover:bg-navy-hover">
          {saving ? "Saving…" : "Add drug"}
        </Button>
      </div>
    </div>
  );
}

// A row seeded from a catalogue drug. Everything the row needs to be counted
// later — the id, the unit, the form — is copied in here, at the one moment
// the link between prescription and catalogue is certain.
function drugRow(d) {
  return {
    key: `${d.id}-${Math.random().toString(36).slice(2, 8)}`,
    drug_name: d.name, drug_id: d.id, form: d.form || "",
    dose: 1, dose_pattern: null,
    dose_unit: doseUnitFor(d),
    frequency: "", intake: "",
    duration_value: null, duration_unit: "day(s)",
    quantity: null, unit: d.unit || "", dispense: false,
    instructions: d.instructions || "",
  };
}

// What one dose is called. Ray prints "Tablet(s)" for a tablet and just
// "Cream" for a cream, because you take one tablet but you do not take one
// cream — anything not counted in pieces reads better as the form itself.
const COUNTABLE = new Set(["tablet", "capsule", "tab", "cap", "sachet", "drop", "puff", "unit"]);
function doseUnitFor(d) {
  const u = (d.unit || "").trim();
  if (u) return COUNTABLE.has(u.toLowerCase()) ? `${cap(u)}(s)` : cap(u);
  const form = (d.form || "").trim();
  return form ? cap(form.toLowerCase()) : "";
}
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* ── Prescription ⇄ catalogue, and the groundwork for inventory ──
 *
 * Nothing decrements stock yet — the inventory module is a later build. What
 * this does is capture, at the moment of prescribing, the three things that
 * cannot be reconstructed afterwards: WHICH catalogue item this is, HOW MANY
 * units it comes to, and whether the clinic is handing it over at all.
 *
 * Re-deriving any of that later from "Tretinoin 0.025% Cream — Apply BD 5 days"
 * is guesswork, and guesswork against a stock count is worse than no count. */

// Units taken per day. A custom pattern says it outright (1-0-1 → 2 doses of
// one unit); otherwise it is the dose times how often the frequency repeats.
function unitsPerDay(row) {
  if (row.dose_pattern) {
    return row.dose_pattern.split("-").reduce((a, b) => a + (Number(b) || 0), 0);
  }
  return (Number(row.dose) || 0) * timesPerDay(row.frequency);
}

// How often a frequency repeats in a day. "as needed" returns 0, which is the
// honest answer — an as-needed course has no computable total, and a guessed
// one is worse than a blank.
function timesPerDay(frequency) {
  const f = String(frequency || "").trim().toLowerCase();
  if (!f || /needed|sos|prn/.test(f)) return 0;
  const hourly = f.match(/every\s+(\d+)\s*hour/);
  if (hourly) return 24 / Number(hourly[1]);
  if (/every other hour/.test(f)) return 12;
  if (/every hour/.test(f)) return 24;
  if (/alternate/.test(f)) return 0.5;
  if (/once a week|weekly/.test(f)) return 1 / 7;
  if (/four times|qid|qds/.test(f)) return 4;
  if (/three times|thrice|tds|tid/.test(f)) return 3;
  if (/twice|bd\b|bid/.test(f)) return 2;
  if (/once|od\b|bedtime|hs\b|immediately|stat/.test(f)) return 1;
  // Bare "1-0-1" typed into the frequency box, as Ray users often do.
  if (/^[\d\s-]+$/.test(f) && f.includes("-")) {
    return f.split("-").reduce((a, b) => a + (Number(b.trim()) || 0), 0);
  }
  return 0;
}

const DURATION_DAYS = { "day(s)": 1, "week(s)": 7, "month(s)": 30 };

// Fill quantity from dose x frequency x duration, but never over a number the
// doctor typed — a hand-entered 30 must survive them correcting the duration.
function withEstimate(row) {
  if (row.qty_touched) return row;
  const days = (Number(row.duration_value) || 0) * (DURATION_DAYS[row.duration_unit] || 1);
  const qty = unitsPerDay(row) * days;
  return { ...row, quantity: qty > 0 ? Math.ceil(qty) : null };
}

// The two human strings the chart and the PDF read. Built once here, on save,
// so the two renderers cannot disagree — and so a Ray row that arrives with
// only these strings still prints.
function renderDosage(row) {
  if (row.dose_pattern) return `${row.dose_pattern}${row.dose_unit ? ` ${row.dose_unit}` : ""}`;
  if (row.dose == null) return "";
  return `${row.dose}${row.dose_unit ? ` ${row.dose_unit}` : ""}`;
}
function renderDuration(row) {
  if (row.duration_value == null) return "";
  return `${row.duration_value} ${row.duration_unit || "day(s)"}`;
}

const splitCsv = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
const num = (v) => (v === "" || v == null ? null : Number(v));
